import React, { FC, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { Room } from "../../app/types/RoomsTypes";
import Button from "../../components/atoms/Button";
import LoaderGlobal from "../../components/molecules/LoaderGlobal";
import { Link, useLoaderData } from "react-router-dom";
import SquareMetersIcon from "../../helpers/icons/SquareMetersIcon";
import CapacityIcon from "../../helpers/icons/CapacityIcon";
import AccessibleIcon from "../../helpers/icons/AccessibleIcon";
import ImageGalleryModal from "../../components/organism/ImageGalleryModal";
import { getFontClass } from "../../helpers/fontUtils";
import HeightIcon from "../../helpers/icons/HeightIcon";
import LengthIcon from "../../helpers/icons/LengthIcon";

const RoomDetailsPage: FC = () => {
  const { room } = useLoaderData() as { room?: Room };
  const { locale, generalSettings } = useSelector(
    (state: RootState) => state.generalSettings,
  );

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (Object.keys(generalSettings).length === 0) {
    return <LoaderGlobal />;
  }

  if (!room) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <h2
          className={`text-2xl font-semibold ${getFontClass(generalSettings.events_styling.font_family)}`}
        >
          Room not found
        </h2>
      </div>
    );
  }

  const mockRoom = room;

  const handleImageClick = () => {
    setCurrentImageIndex(0);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleImageChange = (index: number) => {
    setCurrentImageIndex(index);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev > 0 ? prev - 1 : mockRoom.images.length - 1,
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev < mockRoom.images.length - 1 ? prev + 1 : 0,
    );
  };

  const handleDotClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  return (
    <div
      className={`min-h-screen bg-[#FFFBF5] ${getFontClass(generalSettings.events_styling.font_family)}`}
    >
      {/* Hero Image */}
      <div className="relative w-full h-96 mb-8">
        <img
          src={
            mockRoom.images[currentImageIndex]?.url ||
            "/api/placeholder/800/400"
          }
          alt={mockRoom.name[locale] || mockRoom.name.nb}
          className="w-full h-full object-cover transition-opacity"
          // onClick={handleImageClick}
          style={{
            background:
              "url('data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22800%22 height=%22400%22 viewBox=%220 0 800 400%22><rect width=%22800%22 height=%22400%22 fill=%22%23f3f4f6%22/><defs><pattern id=%22grid%22 width=%2240%22 height=%2240%22 patternUnits=%22userSpaceOnUse%22><path d=%22M 40 0 L 0 0 0 40%22 fill=%22none%22 stroke=%22%23e5e7eb%22 strokeWidth=%221%22/></pattern></defs><rect width=%22800%22 height=%22400%22 fill=%22url(%23grid)%22/><g transform=%22translate(400,200)%22><rect x=%22-60%22 y=%22-20%22 width=%22120%22 height=%2240%22 rx=%224%22 fill=%22%23d1d5db%22/><text x=%220%22 y=%224%22 text-anchor=%22middle%22 font-family=%22Arial%22 font-size=%2214%22 fill=%22%23374151%22>Room Image</text></g></svg>') center/cover",
          }}
        />

        {/* Navigation Arrows */}
        {mockRoom.images.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
              style={{
                backgroundColor: "white",
                borderRadius: "100%",
                color: "#BF5934",
                width: "64px",
                height: "64px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                padding: "0",
              }}
            >
              <span
                style={{
                  fontSize: "64px",
                  fontWeight: "300",
                  lineHeight: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 18,
                  marginRight: 4,
                }}
              >
                ‹
              </span>
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
              style={{
                backgroundColor: "white",
                borderRadius: "100%",
                color: "#BF5934",
                width: "64px",
                height: "64px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                padding: "0",
              }}
            >
              <span
                style={{
                  fontSize: "64px",
                  fontWeight: "300",
                  lineHeight: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 18,
                  marginLeft: 4,
                }}
              >
                ›
              </span>
            </button>
          </>
        )}

        {/* Gallery Button and Dots - Bottom Left */}
        <div className="absolute bottom-6 left-6 flex flex-col items-center space-y-3">
          <Button
            onClick={handleImageClick}
            className={`text-[#BF5934] border-2 bg-white hover:bg-gray-50 transition-colors duration-200 px-8 py-2 shadow-lg pointer-events-auto text-[14px] font-medium ${getFontClass(generalSettings.events_styling.font_family)}`}
          >
            <span>
              {globalThis.translate.em.request.RoomDetailsPage.see_picture}
            </span>
          </Button>

          {/* Dots Indicator */}
          {mockRoom.images.length > 1 && (
            <div className="flex space-x-4">
              {mockRoom.images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 pointer-events-auto ${
                    index === currentImageIndex
                      ? "bg-white"
                      : "bg-white bg-opacity-50 hover:bg-opacity-70"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-4">
            <h1
              className={`text-3xl font-bold mb-6 text-[#2d1810] ${getFontClass(generalSettings.events_styling.font_family)}`}
            >
              {mockRoom.name[locale] || mockRoom.name.nb}
            </h1>

            <div className="prose max-w-none">
              <p
                className={`text-gray-700 leading-relaxed text-base ${getFontClass(generalSettings.events_styling.font_family)}`}
                style={{
                  lineHeight: "1.7",
                }}
              >
                {mockRoom.description[locale] || mockRoom.description.nb}
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2">
            <div className="rounded-lg p-6 space-y-8 bg-white">
              {/* Row 1: Capacity and Square Meters */}
              <div className="grid grid-cols-2 gap-8 ">
                {/* Capacity */}
                <div className="flex items-center space-x-2">
                  <CapacityIcon />
                  <div>
                    <div
                      className={`flex flex-col font-normal text-base text-gray-500 ${getFontClass(generalSettings.events_styling.font_family)}`}
                    >
                      <span>
                        {
                          globalThis.translate.em.request.RoomDetailsPage
                            .capacity
                        }
                        :
                      </span>{" "}
                      <span>
                        {mockRoom.min_capacity}-{mockRoom.max_capacity}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Square Meters */}
                {mockRoom.square_meters && (
                  <div className="flex items-center space-x-2">
                    <SquareMetersIcon />
                    <div>
                      <div
                        className={`flex flex-col font-normal text-base text-gray-500  ${getFontClass(generalSettings.events_styling.font_family)}`}
                      >
                        <span>
                          {
                            globalThis.translate.em.request.RoomDetailsPage
                              .square_meters
                          }{" "}
                        </span>
                        <span>
                          {mockRoom.square_meters}{" "}
                          {globalThis.translate.em.request.RoomDetailsPage.sqm}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-8">
                {/* Height and Length */}
                {mockRoom.height && (
                  <div className="flex items-center space-x-2">
                    <HeightIcon />
                    <div>
                      <div
                        className={`flex flex-col font-normal text-base text-gray-500  ${getFontClass(generalSettings.events_styling.font_family)}`}
                      >
                        <span>
                          {
                            globalThis.translate.em.request.RoomDetailsPage
                              .height
                          }{" "}
                        </span>
                        <span>{mockRoom.height} m</span>
                      </div>
                    </div>
                  </div>
                )}
                {mockRoom.length && (
                  <div className="flex items-center space-x-2">
                    <LengthIcon />
                    <div>
                      <div
                        className={`flex flex-col font-normal text-base text-gray-500 ${getFontClass(generalSettings.events_styling.font_family)}`}
                      >
                        <span>
                          {
                            globalThis.translate.em.request.RoomDetailsPage
                              .length
                          }{" "}
                        </span>
                        <span>{mockRoom.length} m</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Row 2: Accessibility and Request Only */}
              <div className="grid grid-cols-2 gap-8 ">
                {/* Accessibility */}
                {mockRoom.wheelchair_accessibility && (
                  <div className="flex items-center space-x-2">
                    <AccessibleIcon />
                    <div>
                      <div
                        className={`flex flex-col font-normal text-base text-gray-500 uppercase ${getFontClass(generalSettings.events_styling.font_family)}`}
                      >
                        <span>
                          {
                            globalThis.translate.em.request.RoomDetailsPage
                              .accessible
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Request Only */}
                {/* {mockRoom.request_only && (
                  <div className="flex items-center space-x-2">
                    <RequestOnlyIcon />
                    <div>
                      <div className="font-normal text-base text-gray-500 uppercase">
                        {
                          globalThis.translate.em.request.RoomDetailsPage
                            .request_only
                        }
                      </div>
                    </div>
                  </div>
                )} */}
              </div>
            </div>

            <div className="rounded-lg p-6 bg-white mt-8">
              {/* Price Row */}

              {generalSettings.events_styling.room_details_price_show === 0 && (
                <div className="grid grid-cols-2 gap-8 mb-8">
                  <div className="flex items-center space-x-2">
                    {/* <DolarIcon /> */}
                    <div>
                      <div
                        className={`font-normal text-base text-gray-500 ${getFontClass(generalSettings.events_styling.font_family)}`}
                      >
                        {globalThis.translate.em.request.RoomDetailsPage.price}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div
                      className={`font-normal text-base ml-12 ${getFontClass(generalSettings.events_styling.font_family)}`}
                    >
                      {mockRoom?.price} NOK
                    </div>
                  </div>
                </div>
              )}

              {generalSettings.events_styling.room_details_price_show === 1 &&
                mockRoom?.price !== undefined &&
                mockRoom?.price > 0 && (
                  <div className="grid grid-cols-2 gap-8 mb-8">
                    <div className="flex items-center space-x-2">
                      {/* <DolarIcon /> */}
                      <div>
                        <div
                          className={`font-normal text-base text-gray-500 ${getFontClass(generalSettings.events_styling.font_family)}`}
                        >
                          {
                            globalThis.translate.em.request.RoomDetailsPage
                              .price
                          }
                        </div>
                      </div>
                    </div>
                    <div>
                      <div
                        className={`font-normal text-base ml-12 ${getFontClass(generalSettings.events_styling.font_family)}`}
                      >
                        {mockRoom?.price} NOK
                      </div>
                    </div>
                  </div>
                )}

              {/* Send Inquiry Button */}
              <Link to={`/${generalSettings.url}/em/r/${mockRoom.id_room}`}>
                <Button
                  type="button"
                  className={`w-full py-3 px-6 rounded-lg font-normal text-base text-white transition-colors duration-200 bg-[#d2691e] border-none ${getFontClass(generalSettings.events_styling.font_family)}`}
                >
                  {
                    globalThis.translate.em.request.RoomDetailsPage
                      .submit_request
                  }
                </Button>
              </Link>
              <Link to={`/${generalSettings.url}/em/`}>
                <Button
                  type="button"
                  className={`w-full mt-4 py-3 px-6 rounded-lg font-normal text-base text-[#d2691e] transition-colors duration-200 bg-transparent border-2  ${getFontClass(generalSettings.events_styling.font_family)}`}
                >
                  {
                    globalThis.translate.em.request.RoomDetailsPage
                      .back_to_rooms
                  }
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        images={mockRoom.images}
        currentImageIndex={currentImageIndex}
        onImageChange={handleImageChange}
      />
    </div>
  );
};

export default RoomDetailsPage;
